<template>
  <div class="h-full flex flex-col">
    <div class="p-6 pb-0 flex-1 min-h-0 flex flex-col">
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-x-6">
          <h1 class="text-2xl font-bold">API Logs</h1>

          <div v-if="canConfigure" class="flex items-center gap-x-6">
            <div class="flex items-center gap-x-2">
              <InputSwitch
                  v-model="settings.enabled"
                  :disabled="savingSettings"
                  @change="saveSettings"
                  aria-label="Record API requests"
              />
              <span class="text-sm">Record requests</span>
            </div>

            <div class="flex items-center gap-x-2">
              <InputSwitch
                  v-model="settings.only_errors"
                  :disabled="savingSettings || !settings.enabled"
                  @change="saveSettings"
                  aria-label="Record failed requests only"
              />
              <span
                  class="text-sm"
                  :class="settings.enabled ? '' : 'text-surface-400 dark:text-surface-600'"
              >Failed requests only</span>
            </div>
          </div>

          <Tag
              v-else
              :value="settings.enabled ? 'Logging enabled' : 'Logging disabled'"
              :severity="settings.enabled ? 'success' : 'warning'"
          />
        </div>
        <Button label="Refresh" icon="pi pi-refresh" outlined size="small" @click="loadData"/>
      </div>

      <DataTable
          scrollable
          scroll-height="flex"
          :value="apiLogsStore.getApiLogs"
          lazy
          paginator
          :rows="params.per_page"
          :rowsPerPageOptions="[15, 25, 50, 100]"
          :total-records="apiLogsStore.data?.total || 0"
          :loading="loading"
          @page="onPage($event)"
          @sort="onSort($event)"
          @row-click="openDetail($event.data)"
          data-key="id"
          size="small"
          removable-sort
          always-show-paginator
          row-hover
          class="-mx-6 flex-1 min-h-0"
      >
        <template #header>
          <Toolbar class="border-none shadow-none -m-3.5 rounded-none">
            <template #start>
              <div class="flex flex-wrap items-center gap-2">
                <IconField icon-position="left">
                  <InputIcon>
                    <IconSearch class="text-surface-400 dark:text-surface-600 h-4 w-4"/>
                  </InputIcon>
                  <InputText
                      placeholder="Search path, user, IP, error..."
                      class="sm:py-2 w-72"
                      v-model="filters.search"
                      @keyup="onFilterChange"
                  />
                </IconField>

                <Dropdown
                    v-model="filters.user_id"
                    :options="userOptions"
                    option-label="name"
                    option-value="id"
                    placeholder="Any user"
                    class="w-52"
                    filter
                    show-clear
                    :loading="loadingUsers"
                    @filter="onUserFilter"
                    @change="onFilterChange"
                >
                  <template #option="{option}">
                    <div class="flex flex-col">
                      <span>{{ option.name }}</span>
                      <span class="text-xs text-surface-500">{{ option.email }}</span>
                    </div>
                  </template>
                </Dropdown>

                <Dropdown
                    v-model="filters.method"
                    :options="methodOptions"
                    placeholder="Any method"
                    class="w-40"
                    show-clear
                    @change="onFilterChange"
                />

                <Dropdown
                    v-model="filters.status_class"
                    :options="statusOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Any status"
                    class="w-44"
                    show-clear
                    @change="onFilterChange"
                />

                <Calendar
                    v-model="filters.created_after"
                    placeholder="From"
                    class="w-44"
                    show-time
                    hour-format="24"
                    date-format="dd/mm/yy"
                    show-button-bar
                    @date-select="onFilterChange"
                    @clear-click="onFilterChange"
                />

                <Calendar
                    v-model="filters.created_before"
                    placeholder="To"
                    class="w-44"
                    show-time
                    hour-format="24"
                    date-format="dd/mm/yy"
                    show-button-bar
                    @date-select="onFilterChange"
                    @clear-click="onFilterChange"
                />

                <Button
                    v-if="hasActiveFilters"
                    label="Clear"
                    icon="pi pi-filter-slash"
                    text
                    size="small"
                    @click="clearFilters"
                />
              </div>
            </template>
          </Toolbar>
        </template>

        <Column field="created_at" header="Time" sortable class="pl-6 lg:pl-8 w-52">
          <template #body="{data}">{{ formatDate(data.created_at) }}</template>
        </Column>

        <Column field="method" header="Method" class="w-28">
          <template #body="{data}">
            <Tag :value="data.method" :severity="methodSeverity(data.method)"/>
          </template>
        </Column>

        <Column field="path" header="Path" sortable>
          <template #body="{data}">
            <div class="flex flex-col">
              <span class="font-mono text-xs">/{{ data.path }}</span>
              <span v-if="data.route_name" class="text-xs text-surface-500 dark:text-surface-400">
                {{ data.route_name }}
              </span>
            </div>
          </template>
        </Column>

        <Column field="status" header="Status" sortable class="w-28">
          <template #body="{data}">
            <Tag :value="String(data.status)" :severity="statusSeverity(data.status)"/>
          </template>
        </Column>

        <Column field="duration_ms" header="Duration" sortable class="w-28">
          <template #body="{data}">{{ data.duration_ms === null ? '-' : `${data.duration_ms} ms` }}</template>
        </Column>

        <Column field="user" header="User" class="w-52">
          <template #body="{data}">{{ data.user?.name || 'Guest' }}</template>
        </Column>

        <Column field="ip" header="IP" class="w-36">
          <template #body="{data}">{{ data.ip || '-' }}</template>
        </Column>

        <Column field="exception_class" header="Error" class="w-52">
          <template #body="{data}">
            <span v-if="data.exception_class" class="text-xs text-red-600 dark:text-red-400">
              {{ shortClass(data.exception_class) }}
            </span>
            <span v-else>-</span>
          </template>
        </Column>

        <template #empty>
          <div class="text-center py-8 text-surface-500 dark:text-surface-400">
            <p v-if="!settings.enabled && canConfigure">
              API logging is currently switched off. Turn on "Record requests" above to start recording.
            </p>
            <p v-else-if="!settings.enabled">
              API logging is currently switched off.
            </p>
            <p v-else>No API requests match these filters.</p>
          </div>
        </template>
      </DataTable>
    </div>

    <Dialog
        v-model:visible="detailVisible"
        modal
        maximizable
        header="API Request"
        :style="{ width: '64rem' }"
        :breakpoints="{ '1199px': '85vw', '575px': '95vw' }"
    >
      <div v-if="detail" class="space-y-4">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div class="text-xs text-surface-500 dark:text-surface-400">Method &amp; status</div>
            <div class="flex items-center gap-2 mt-1">
              <Tag :value="detail.method" :severity="methodSeverity(detail.method)"/>
              <Tag :value="String(detail.status)" :severity="statusSeverity(detail.status)"/>
            </div>
          </div>
          <div>
            <div class="text-xs text-surface-500 dark:text-surface-400">Time</div>
            <div class="mt-1">{{ formatDate(detail.created_at) }}</div>
          </div>
          <div>
            <div class="text-xs text-surface-500 dark:text-surface-400">Duration</div>
            <div class="mt-1">{{ detail.duration_ms === null ? '-' : `${detail.duration_ms} ms` }}</div>
          </div>
          <div>
            <div class="text-xs text-surface-500 dark:text-surface-400">User</div>
            <div class="mt-1">{{ detail.user?.name || 'Guest' }}</div>
          </div>
          <div class="col-span-2 md:col-span-4">
            <div class="text-xs text-surface-500 dark:text-surface-400">Path</div>
            <div class="mt-1 font-mono text-xs break-all">/{{ detail.path }}</div>
          </div>
          <div class="col-span-2">
            <div class="text-xs text-surface-500 dark:text-surface-400">Request ID (Sentry tag)</div>
            <div class="mt-1 font-mono text-xs break-all">{{ detail.request_id }}</div>
          </div>
          <div class="col-span-2">
            <div class="text-xs text-surface-500 dark:text-surface-400">IP / user agent</div>
            <div class="mt-1 text-xs break-all">{{ detail.ip || '-' }} &mdash; {{ detail.user_agent || '-' }}</div>
          </div>
        </div>

        <Message v-if="detail.exception_class" severity="error" :closable="false">
          <span class="font-mono text-xs">{{ detail.exception_class }}</span>
          <div class="text-xs mt-1">{{ detail.exception_message }}</div>
        </Message>

        <TabView>
          <TabPanel header="Request">
            <div class="text-xs text-surface-500 dark:text-surface-400 mb-1">Query</div>
            <pre class="log-body">{{ pretty(detail.query) }}</pre>
            <div class="text-xs text-surface-500 dark:text-surface-400 mb-1 mt-3">Body</div>
            <pre class="log-body">{{ pretty(detail.request_body) }}</pre>
          </TabPanel>
          <TabPanel header="Response">
            <div class="text-xs text-surface-500 dark:text-surface-400 mb-1">
              {{ detail.response_content_type || 'unknown content type' }}
              <span v-if="detail.response_size"> &mdash; {{ detail.response_size }} bytes</span>
            </div>
            <pre class="log-body">{{ pretty(detail.response_body) }}</pre>
          </TabPanel>
          <TabPanel header="Headers">
            <pre class="log-body">{{ pretty(detail.request_headers) }}</pre>
          </TabPanel>
        </TabView>
      </div>

      <template #footer>
        <Button
            v-if="$auth.hasPermission('delete:api-logs')"
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            text
            @click="deleteDetail"
        />
        <Button label="Close" @click="detailVisible = false"/>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useApiLogsStore } from "@/stores/apiLogs.js";
import { IconSearch } from "@tabler/icons-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputSwitch from "primevue/inputswitch";
import dayjs from 'dayjs';
import { debounce } from 'lodash';

export default {
  name: 'ApiLogsIndex',
  components: {
    IconSearch,
    IconField,
    InputIcon,
    InputSwitch,
  },
  data() {
    return {
      loading: false,
      loadingUsers: false,
      savingSettings: false,
      detailVisible: false,
      detail: null,
      settings: {
        enabled: false,
        only_errors: false,
      },
      userOptions: [],
      methodOptions: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
      statusOptions: [
        {label: '2xx Success', value: '2'},
        {label: '3xx Redirect', value: '3'},
        {label: '4xx Client error', value: '4'},
        {label: '5xx Server error', value: '5'},
        {label: 'Any error', value: 'failed'},
      ],
      filters: {
        search: '',
        user_id: null,
        method: null,
        status_class: null,
        created_after: null,
        created_before: null,
      },
      params: {
        page: 1,
        per_page: 25,
        sort: '-created_at',
      },
    }
  },
  computed: {
    ...mapStores(useApiLogsStore),
    canConfigure() {
      return this.$auth.hasPermission('update:api-logs');
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(value => value !== null && value !== '');
    },
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await this.apiLogsStore.indexApiLogs({
          ...this.params,
          include: ['user'],
          filter: this.activeFilters(),
        });
      } finally {
        this.loading = false;
      }
    },
    /**
     * Build the filter payload, dropping anything unset. Values are sent as
     * strings because the API validates filters as strings.
     */
    activeFilters() {
      const filter = {};

      if (this.filters.search) {
        filter.search = this.filters.search;
      }
      if (this.filters.user_id) {
        filter.user_id = String(this.filters.user_id);
      }
      if (this.filters.method) {
        filter.method = this.filters.method;
      }
      if (this.filters.status_class === 'failed') {
        filter.failed = '1';
      } else if (this.filters.status_class) {
        filter.status_class = this.filters.status_class;
      }
      if (this.filters.created_after) {
        filter.created_after = dayjs(this.filters.created_after).format('YYYY-MM-DD HH:mm:ss');
      }
      if (this.filters.created_before) {
        filter.created_before = dayjs(this.filters.created_before).format('YYYY-MM-DD HH:mm:ss');
      }

      return filter;
    },
    onFilterChange: debounce(function () {
      this.params.page = 1;
      this.loadData();
    }, 300),
    onPage(event) {
      this.params.page = event.page + 1;
      this.params.per_page = event.rows;
      this.loadData();
    },
    onSort(event) {
      const field = event.sortField || 'created_at';
      this.params.sort = event.sortOrder === 1 ? field : `-${field}`;
      this.loadData();
    },
    clearFilters() {
      this.filters = {
        search: '',
        user_id: null,
        method: null,
        status_class: null,
        created_after: null,
        created_before: null,
      };
      this.params.page = 1;
      this.loadData();
    },
    onUserFilter: debounce(async function (event) {
      await this.loadUserOptions(event.value);
    }, 300),
    async loadUserOptions(search = '') {
      this.loadingUsers = true;
      try {
        this.userOptions = await this.apiLogsStore.indexUserOptions(search);
      } finally {
        this.loadingUsers = false;
      }
    },
    async loadSettings() {
      this.settings = await this.apiLogsStore.showApiLogSettings();
    },
    /**
     * Settings are stored server side, so the change applies to every running
     * instance on their next request - no redeployment involved.
     */
    async saveSettings() {
      this.savingSettings = true;
      try {
        this.settings = await this.apiLogsStore.updateApiLogSettings({
          enabled: this.settings.enabled,
          only_errors: this.settings.only_errors,
        });
        await this.loadData();
      } catch (error) {
        // Put the switches back to whatever the server actually has.
        await this.loadSettings();
      } finally {
        this.savingSettings = false;
      }
    },
    async openDetail(row) {
      this.detail = null;
      this.detailVisible = true;
      this.detail = await this.apiLogsStore.showApiLog(row.id);
    },
    async deleteDetail() {
      await this.apiLogsStore.deleteApiLog(this.detail.id);
      this.detailVisible = false;
      await this.loadData();
    },
    /**
     * Pretty print a stored JSON string, falling back to the raw value for
     * bodies that are not JSON (HTML error pages, plain text).
     */
    pretty(value) {
      if (value === null || value === undefined || value === '') {
        return '(empty)';
      }

      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (error) {
        return value;
      }
    },
    formatDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY HH:mm:ss') : '-';
    },
    shortClass(name) {
      return name ? name.split('\\').pop() : '';
    },
    methodSeverity(method) {
      return {
        GET: 'info',
        POST: 'success',
        PUT: 'warning',
        PATCH: 'warning',
        DELETE: 'danger',
      }[method] || 'secondary';
    },
    statusSeverity(status) {
      if (status >= 500) return 'danger';
      if (status >= 400) return 'warning';
      if (status >= 300) return 'info';
      return 'success';
    },
  },
  async mounted() {
    await Promise.all([
      this.loadData(),
      this.loadSettings(),
      this.loadUserOptions(),
    ]);
  }
}
</script>

<style scoped>
.log-body {
  @apply bg-surface-100 dark:bg-surface-800 rounded p-3 text-xs font-mono overflow-auto max-h-80 whitespace-pre-wrap break-all;
}

:deep(.p-datatable-loading-icon) {
  z-index: 10;
}

:deep(.p-datatable-wrapper) {
  min-height: 200px;
}

:deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}
</style>
